import { type FC, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import DoneIcon from '@assets/img/icons/done.svg?react';
import { AnimatePresence, motion } from 'framer-motion';

import { useClaimThankYouRewardMutation } from '@/api/queries/user';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { type ThankForPurchaseRequestResponseType } from '@/common/types/query/user';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  H1,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { MotionPicture, Picture } from '@/components/ui/Picture/Picture';

import styles from './styles.module.scss';

import tankYouChracterPng from '/img/characters/thank-you-character.png';
import tankYouChracterWebp from '/img/characters/thank-you-character.webp';
import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';
import tgem3dPng from '/img/icons/tgem-3d.png';
import tgem3dWebp from '/img/icons/tgem-3d.webp';
import lightPng from '/img/patterns/light.png';
import lightWebp from '/img/patterns/light.webp';

const icons: Record<
  keyof ThankForPurchaseRequestResponseType,
  { png: string; webp: string }
> = {
  tgem: {
    png: tgem3dPng,
    webp: tgem3dWebp,
  },
  dice: {
    png: dicePng,
    webp: diceWebp,
  },
};

const ThanksForPurchase: FC<ThankForPurchaseRequestResponseType> = (props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'1' | '2' | '3'>('1');
  const { closeModal } = useContext(ModalContext);
  const [mutate, { isLoading }] = useClaimThankYouRewardMutation();

  const handleClaim = async (): Promise<void> => {
    try {
      await mutate(props).unwrap();
      setStep('2');
    } catch {
      ToastNotification(t('Something went wrong, please try again'));
    }
  };

  const keys = Object.keys(
    props,
  ) as (keyof ThankForPurchaseRequestResponseType)[];

  return (
    <div
      className="flex items-center flex-col pt-[var(--header-height)] h-full"
      onClick={() => {
        if (step === '3') {
          closeModal();
        }
      }}
    >
      <Picture
        img={tankYouChracterPng}
        webp={tankYouChracterWebp}
        alt="tankYouChracter"
        className={styles.character}
      />
      <H1 weight={'extrabold'}>{t('Thanks for purchase!')}</H1>
      <StringSmall className="max-w-[18.625rem] text-center !text-white/80 leading-[130%] mt-2 !font-light">
        <Trans
          i18nKey={
            'We appreciate your contribution to the development of Star Industry and have prepared a small gift from our team'
          }
          components={{ br: <br /> }}
        />
      </StringSmall>

      <AnimatePresence mode="wait">
        {step !== '3' && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center flex-col"
          >
            <div className="mt-[1.375rem] flex items-center justify-center">
              <div className={styles.purpleLight} />
              <Picture
                img={lightPng}
                webp={lightWebp}
                alt="light"
                className={styles.light}
              />
              {keys.map((key) => (
                <MotionPicture
                  animate={{
                    scale: step === '2' ? 1.25 : 1,
                  }}
                  img={icons[key].png}
                  webp={icons[key].png}
                  alt="tgem3d"
                  className="size-24"
                />
              ))}
            </div>

            <span className="mt-4 text-[2.5rem] font-extrabold text-center leading-none">
              {keys.map((key) => '+' + props[key]).join(' ')}
            </span>

            <MainButton
              isLoading={isLoading}
              onClick={handleClaim}
              text={t('claim')}
              className="h-12 mt-6 !w-[10.625rem] [&_h2]:text-lg"
            />
          </motion.div>
        )}
        {step === '3' && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-[4.125rem]"
            >
              <DoneIcon className="size-16" />
            </motion.div>
            <motion.div
              className="mt-[5.25rem]"
              initial={{ opacity: 0 }}
              transition={{ delay: 0.15 }}
              animate={{ opacity: 1 }}
            >
              <StringMain weight={'extrabold'}>
                {t('Tap to continue')}
              </StringMain>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {step === '2' && (
        <Confetti
          className="z-10 w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          loop={false}
          autoPlay={true}
          speed={2}
          onComplete={() => setStep('3')}
          width="100%"
          height="100%"
        />
      )}
    </div>
  );
};

export default ThanksForPurchase;
